import React from 'react';
import { PreviousIcon, NextIcon, FirstIcon, LastIcon } from '.';

/**
 * PaginatorV2 Component
 *
 * A reusable pagination component designed to handle navigation across multiple pages.
 * It dynamically adapts to different scenarios (single page, few pages, and many pages).
 *
 * Props:
 * - `getPaginatedPageNumbers` (Array, required): An array of numbers and ellipsis (...) representing the paginated page structure.
 * - `currentPage` (number, required): The current active page.
 * - `onPageChange` (Function, required): Callback function triggered when a page is selected.
 * - `total` (number, required): Total number of pages available for pagination.
 * - `goToPrevPage` (Function, required): Function to navigate to the previous page.
 * - `goToNextPage` (Function, required): Function to navigate to the next page.
 */
const PaginatorV2 = ({
  getPaginatedPageNumbers,
  currentPage,
  onPageChange,
  total,
  goToPrevPage,
  goToNextPage,
}) => {
  const isSinglePage = total === 1;
  const isFewPages = total <= 5;
  const disableNavigation = isSinglePage || isFewPages;

  const isOnFirstPage = currentPage === 1;
  const isOnLastPage = currentPage === total;

  const buttonClass = 'px-2 py-1 rounded-lg disabled:text-gray-300';
  const activePageClass = 'bg-[#F2F6FA] text-custom-light-blue !py-0';
  const ellipsisClass = '!px-0 !text-black';

  const renderButton = (icon, onClickHandler, isDisabled, extraClasses = '', ariaLabel = '') => (
    <button
      onClick={onClickHandler}
      disabled={isDisabled}
      className={`${buttonClass} ${extraClasses}`}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  );

  const handlePageClick = (page) => {
    if (page !== '...' && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex justify-center mt-8">
      <div className="flex items-center px-1 py-0 bg-white rounded-lg">
        {renderButton(
          <FirstIcon />,
          () => onPageChange(1),
          disableNavigation || isOnFirstPage,
          '',
          'First page',
        )}

        {renderButton(
          <PreviousIcon />,
          goToPrevPage,
          isSinglePage || isOnFirstPage,
          disableNavigation ? 'mr-6' : '',
          'Previous page',
        )}

        {getPaginatedPageNumbers.map((page, index) => {
          const isActivePageClass = currentPage === page ? activePageClass : 'text-black';
          const isEllipsisClass = page === '...' ? ellipsisClass : '';
          const computedClassName = `${buttonClass} ${isActivePageClass} ${isEllipsisClass}`.trim();

          return (
            <button
              key={typeof page === 'string' ? `ellipsis-${index}` : page}
              onClick={() => handlePageClick(page)}
              disabled={page === '...'}
              className={computedClassName}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          );
        })}

        {renderButton(
          <NextIcon />,
          goToNextPage,
          isSinglePage || isOnLastPage,
          disableNavigation ? 'ml-6' : '',
          'Next page',
        )}

        {renderButton(
          <LastIcon />,
          () => onPageChange(total),
          disableNavigation || isOnLastPage,
          '',
          'Last page',
        )}
      </div>
    </div>
  );
};

export default PaginatorV2;
